import {getImporter} from "../../../lib/wineRequests";
import {useRouter} from "next/router";
import React, {useEffect, useState} from "react";
import {Importer} from "../../../context/wineContext/reducer";
import {formatTitle} from "../../../utils/helper";
import Head from "next/head";
import Loading from "../../global/Loading";
import ProducerResult from "../../global/BaseWines/ProducerResult";
import Pagination from "../../global/Pagination/Pagination";
import {loadProducers, useWineContext} from "../../../context/wineContext";
import Image from "next/image";

export default function ImportersDetails() {
  const router = useRouter();
  const {slug} = router.query;
  const [loading, setLoading] = useState(false);
  const [firstLoad, setFirstLoad] = useState(true);
  const [importer, setImporter] = useState({} as Importer);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  const [sortDirection, setSortDirection] = useState(true);

  useEffect(() => {
    if (firstLoad && slug) {
      loadImporter();
      setFirstLoad(false);
    }
  }, [slug])

  useEffect(() => {
    if (!firstLoad) {
      loadImporterProducers();
    }
  }, [importer, sortDirection, page, perPage])

  const loadImporter = async () => {
    setLoading(true);
    const response = await getImporter(slug as string)
    if (response) {
      setImporter(response.data.importer);
    }
    setLoading(false);
  }

  const {
    state: { loading: producersLoading, producers, producersTotal },
    dispatch: winesDispatch,
  } = useWineContext();

  const handlePageChange = (e) => {
    setPage(e.page);
    setPerPage(e.perPage);
  }

  const loadImporterProducers = async () => {
    if(slug) {
      router.push({
        pathname: '/importers/' + slug,
        query: {
          sort: "producer_name",
          sortDirection: sortDirection,
          page: page,
          perPage: perPage,
        }
      }, undefined, { shallow: true })
    }

    loadProducers({
      term: "",
      "filters[importer_id]": importer.id,
      order: "producer_name",
      order_type: sortDirection ? "asc" : "desc",
      page: page,
      per_page: perPage
    }, winesDispatch);
  }
  return (
    <>
      {
        loading &&
          <Loading isLoading={loading}/>
      }
      {importer?.name && (
        <>
          <Head>
            <title>{formatTitle(importer.name)}</title>
            <meta name="importer_country" content={importer.country}/>
            <meta name="importer_website" content={importer.website}/>
          </Head>
          <h1 className="text-3xl font-bold pt-4 pb-8 text-center">
            {importer.name}
          </h1>
          <div className="lg:flex lg:items-start lg:space-x-8 relative">
            <div className="w-full lg:w-1/4 space-y-8">
              <div>
                <div className="font-light text-xl border-b border-gray-100 pb-2 mb-4">
                  Importer Details
                </div>

                <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-1">
                  {importer.description && (
                    <div>
                      <div className="text-sm">
                        About
                      </div>
                      <p className="font-light">
                        {importer.description}
                      </p>
                    </div>
                  )}
                  <div>
                    {!importer?.image_url.includes('missing.png') && <div className="aspect-square bg-cover bg-center hover relative overflow-hidden h-24 w-24">
                      <Image
                        src={importer.image_url}
                        alt={`${importer.name} logo`}
                        fill
                        sizes="(min-width: 640px) 50vw, 100vw"
                        style={{objectFit: "contain"}}
                      />
                    </div>}
                  </div>
                  <div>
                    <div className="text-sm">
                      Imports To
                    </div>
                    <p className="text-red">
                      {importer.country}
                    </p>
                  </div>
                  {importer.address && (
                    <div>
                      <div className="text-sm">
                        Address
                      </div>
                      <p className="text-red">
                        {importer.address}
                      </p>
                    </div>
                  )}
                  {importer.telephone && (
                    <div>
                      <div className="text-sm">
                        Phone
                      </div>
                      <p className="text-red">
                        {importer.telephone}
                      </p>
                    </div>
                  )}
                  {importer.email && (
                    <div>
                      <div className="text-sm">
                        Email
                      </div>
                      <p className="text-red">
                        {importer.email}
                      </p>
                    </div>
                  )}
                  {importer.website && (
                    <div>
                      <div className="text-sm">
                        Website
                      </div>
                      <a className="text-red" target="_blank" rel="noreferrer noopener" href={importer.website}>{importer.website}</a>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="w-full lg:w-3/4 space-y-2">
              <div className="font-light text-xl border-b border-gray-100 pb-2 mb-4">
                Producers Imported
              </div>
              <div
                className="transition cursor-pointer ml-5 hover:text-red hover:opacity-100 flex justify-end"
                onClick={() => {
                  setSortDirection(!sortDirection);
                }}
                onKeyDown={(e) => e.key === 'Enter' && setSortDirection(!sortDirection)}
                role="button"
                tabIndex={0}
                aria-label="Sort"
              >
                {
                  sortDirection ?
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
                    </svg>
                    :
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h9m5-4v12m0 0l-4-4m4 4l4-4" />
                    </svg>
                }
              </div>
              {producersLoading && (
                <Loading isLoading={loading}/>
              )}
              {producers && producers.length > 0 ? (
                <div>
                  {producers &&
                    producers.map(prod => {
                      return (
                        <ProducerResult
                          key={prod.slug}
                          producer={prod}
                        />
                      )
                    })
                  }

                  {
                    producers &&
                    producersTotal > 25 &&
                      <Pagination
                          records={producersTotal}
                          onPageChange={(e) => handlePageChange(e)}
                      />
                  }
                </div>
              ) : (
                <div className="text-center pt-32 text-lg">No producers found.</div>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
}
